// ModelLayer - the host model's tracked elements on the overview (SpaceX BA03
// step 3; server: utils/glance/trackedGlance, GET /glance/tracked). One
// category at a time is drawn on the floor plates as the cells it occupies;
// counts come with it. When the model cannot be placed yet, the reason is
// said instead (no grid on the model, nothing aligned, boxes not computed).
import React, { useEffect, useState } from "react";
import { Segmented } from "antd";
import { url, tokenRef } from "api";

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem(tokenRef)}` });
const CATEGORIES = [["racks", "Racks"], ["busway", "Busway"], ["cableTray", "Cable tray"], ["conduitsAll", "Conduits"], ["singleLine", "Single line"], ["branch", "Branch"]];
const COLOR = { racks: "#7C3AED", busway: "#C2410C", cableTray: "#0E7490", conduitsAll: "#1D4ED8", singleLine: "#1E3A8A", branch: "#2563EB" };
const n0 = (v) => Math.round(Number(v) || 0).toLocaleString("en-US");

// { racks: 31632, ... } summed over levels, for the categories the model has.
export function categoryTotals(byLevel) {
  const out = {};
  Object.values(byLevel || {}).forEach((b) => Object.entries(b).forEach(([k, v]) => { out[k] = (out[k] || 0) + (v.n || 0); }));
  return out;
}

// The layer ExplodedStack draws for one category: cells per level.
export function layerFor(tracked, key) {
  if (!tracked || tracked.status !== "done" || !key) return null;
  const byLevel = {};
  Object.entries(tracked.byLevel || {}).forEach(([L, b]) => { if (b[key]) byLevel[L] = b[key].cells || []; });
  return { byLevel, cellFt: tracked.cellFt || 5, color: COLOR[key] || "#1D4ED8" };
}

export default function ModelLayer({ projectId, onLayer }) {
  const [tracked, setTracked] = useState(null);
  const [key, setKey] = useState("");
  useEffect(() => {
    if (!projectId) return undefined;
    let alive = true;
    fetch(`${url}/projects/${projectId}/glance/tracked`, { headers: auth() })
      .then((r) => (r.ok ? r.json() : { tracked: null })).then((b) => { if (alive) setTracked(b.tracked); }).catch(() => { if (alive) setTracked(null); });
    return () => { alive = false; };
  }, [projectId]);
  useEffect(() => { onLayer(layerFor(tracked, key)); }, [tracked, key, onLayer]);

  if (!tracked) return null;
  if (tracked.status !== "done") return <p className="g-note">Model elements are not on this overview yet: {tracked.reason}.</p>;
  const totals = categoryTotals(tracked.byLevel);
  const options = [{ label: "Off", value: "" }, ...CATEGORIES.filter(([k]) => totals[k]).map(([k, l]) => ({ label: `${l} (${n0(totals[k])})`, value: k }))];
  const off = (tracked.unplaced && (tracked.unplaced.offGrid || 0) + (tracked.unplaced.noLevel || 0)) || 0;
  return (
    <div className="g-ctrl g-noprint">
      <span>From the model</span>
      <Segmented size="small" value={key} onChange={setKey} options={options} />
      <span className="g-tsub">
        Placed on the drawings' grid by {tracked.gridSource || "the model's grid"}{off ? `; ${n0(off)} elements off the grid or on a level the drawings do not show` : ""}.
      </span>
    </div>
  );
}
